import {
  Autocomplete,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Select,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MenuItem from "@mui/material/MenuItem";
import MDTypography from "components/MDTypography";
import React, { useEffect } from "react";
import { checkPermission } from "utils/HelperFunctions";
import { PERMISSIONS_NAME } from "lib/constants";
import { useSelector } from "react-redux";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    sx: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 100,
      "& .MuiMenuItem-root": {
        minWidth: "100%",
        width: "100%",
      },
    },
  },
};

function CompetitionInfo({
  formData,
  badges,
  badgeLoading,
  types,
  typesLoading,
  campaigns,
  campaignsLoading,
  previousCampaignId,
}) {
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const { values, errors, touched, setFieldValue } = formData;
  const {
    name: nameV,
    badge: badgeV,
    description: descriptionV,
    type: typeV,
    reward: rewardV,
    campaignId: campaignIdV,
  } = values.info;

  const showTremendousCampaignField = typeV === "Tremendous payout";

  useEffect(() => {
    if (previousCampaignId && campaigns) {
      setFieldValue(
        "info.campaignId",
        campaigns.find((campaign) => campaign.id === previousCampaignId)?.name || ""
      );
    }
  }, [previousCampaignId, campaigns]);

  return (
    <MDBox>
      <MDBox>
        <Grid container spacing={3} p={1}>
          <Grid item xs={12} sm={12}>
            <MDBox>
              <MDTypography variant="h5" fontWeight="bold">
                Competition Info
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <MDInput
              variant="standard"
              label="Name"
              value={nameV}
              name="name"
              placeholder="Name"
              fullWidth
              error={touched?.info?.name && Boolean(errors?.info?.name)}
              helperText={touched?.info?.name && errors?.info?.name}
              onChange={(event) => setFieldValue("info.name", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <MDInput
              variant="standard"
              label="Description"
              value={descriptionV}
              name="description"
              placeholder="Description"
              fullWidth
              error={touched?.info?.description && Boolean(errors?.info?.description)}
              helperText={touched?.info?.description && errors?.info?.description}
              onChange={(event) => setFieldValue("info.description", event.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Autocomplete
              fullWidth
              id="type"
              disableClearable
              options={types}
              value={typeV}
              onChange={(_, value) => {
                if (
                  (checkPermission(PERMISSIONS_NAME?.COMPETE_UNRANKED, loginUser?.userPermission) &&
                    checkPermission(
                      PERMISSIONS_NAME?.COMPETE_TREMENDOUS_PAYOUT,
                      loginUser?.userPermission
                    )) ||
                  loginUser.type !== "user"
                ) {
                  setFieldValue("info.type", value);
                }
              }}
              disabled={
                loginUser.type === "user" &&
                !(
                  checkPermission(PERMISSIONS_NAME?.COMPETE_UNRANKED, loginUser?.userPermission) &&
                  checkPermission(
                    PERMISSIONS_NAME?.COMPETE_TREMENDOUS_PAYOUT,
                    loginUser?.userPermission
                  )
                )
              }
              renderInput={(params) => (
                <MDInput
                  {...params}
                  variant="standard"
                  placeholder="Type"
                  label="Type"
                  error={touched?.info?.type && Boolean(errors?.info?.type)}
                  helperText={touched?.info?.type && errors?.info?.type}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {typesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {touched?.info?.type && errors?.info?.type ? (
              <FormHelperText>{errors.info.type}</FormHelperText>
            ) : null}
          </Grid>
          <Grid item xs={12} sm={12} md={2}>
            <FormControl fullWidth className="badge-input">
              <InputLabel id="badge-label">Badge</InputLabel>
              <Select
                fullWidth
                sx={{ height: "30px" }}
                // error={touched?.info?.badge && Boolean(errors?.info?.badge)}
                // helperText={touched?.info?.badge && errors?.info?.badge}
                labelId="badge-label"
                id="color"
                value={badgeV || ""}
                onChange={(_) => {
                  setFieldValue("info.badge", _?.target?.value);
                }}
                variant="standard"
                endAdornment={badgeLoading ? <CircularProgress color="inherit" size={20} /> : null}
                renderValue={(selected) => (
                  <MDBox
                    sx={{
                      borderRadius: "50%",
                      margin: "5px",
                      img: {
                        height: "35px",
                        width: "35px",
                        marginBottom: "6px",
                      },
                    }}
                  >
                    {!badgeLoading ? (
                      <img
                        src={badges.find((badge) => badge?.id === selected)?.badgeUrl}
                        alt={`badge-${selected.id}`}
                      />
                    ) : null}
                  </MDBox>
                )}
                MenuProps={MenuProps}
              >
                {badges.length === 0 ? (
                  <MenuItem value={null}>
                    <MDTypography sx={{ fontSize: "16px" }}>
                      {badgeLoading ? "Loading..." : "No Options"}
                    </MDTypography>
                  </MenuItem>
                ) : (
                  badges.map((badge) => (
                    <MenuItem key={badge?.id} value={badge?.id}>
                      <MDBox
                        sx={{
                          borderRadius: "50%",
                          margin: "5px",
                          img: {
                            height: "40px",
                            width: "40px",
                          },
                        }}
                      >
                        <img src={badge?.badgeUrl} alt={`badge-${badge.id}`} />
                      </MDBox>
                    </MenuItem>
                  ))
                )}
              </Select>
              {touched?.info?.badge && Boolean(errors?.info?.badge) && (
                <FormHelperText
                  sx={{
                    "&.MuiFormHelperText-root": {
                      marginRight: 0,
                      marginLeft: 0,
                    },
                  }}
                >
                  {touched?.info?.badge && errors?.info?.badge}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={showTremendousCampaignField ? 3 : 6}>
            <MDInput
              variant="standard"
              label="Reward"
              value={rewardV}
              name="reward"
              placeholder="Reward"
              error={touched?.info?.reward && Boolean(errors?.info?.reward)}
              helperText={touched?.info?.reward && errors?.info?.reward}
              onChange={(event) => setFieldValue("info.reward", event.target.value)}
              fullWidth
            />
            {touched?.info?.reward && Boolean(errors?.info?.reward) && (
              <FormHelperText
                sx={{
                  "&.MuiFormHelperText-root": {
                    marginRight: 0,
                    marginLeft: 0,
                  },
                }}
              >
                {touched?.info?.reward && errors?.info?.reward}
              </FormHelperText>
            )}
          </Grid>
          {showTremendousCampaignField && (
            <Grid item xs={12} sm={3} md={3}>
              <FormControl fullWidth className="tremendous-campaign-input">
                <InputLabel id="tremendous-campaign-label">Tremendous Campaign</InputLabel>
                <Select
                  fullWidth
                  labelId="tremendous-campaign-label"
                  id="tremendous-campaign-id"
                  value={campaignIdV || ""}
                  onChange={(event) => setFieldValue("info.campaignId", event.target.value)}
                  variant="standard"
                  renderValue={(selected) => (
                    <MDBox
                      sx={{
                        borderRadius: "50%",
                        margin: "5px",
                        img: {
                          height: "35px",
                          width: "35px",
                          marginBottom: "6px",
                        },
                      }}
                    >
                      {!campaignsLoading ? selected : null}
                    </MDBox>
                  )}
                >
                  {campaigns.length === 0 ? (
                    <MenuItem value={null}>
                      <MDTypography sx={{ fontSize: "16px" }}>
                        {campaignsLoading ? "Loading..." : "No Options"}
                      </MDTypography>
                    </MenuItem>
                  ) : (
                    campaigns.map((campaign) => (
                      <MenuItem key={campaign?.id} value={campaign?.name}>
                        {campaign.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {touched?.info?.campaignId && Boolean(errors?.info?.campaignId) && (
                  <FormHelperText
                    sx={{
                      "&.MuiFormHelperText-root": {
                        marginRight: 0,
                        marginLeft: 0,
                      },
                    }}
                  >
                    {touched?.info?.campaignId && errors?.info?.campaignId}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
          )}
        </Grid>
      </MDBox>
    </MDBox>
  );
}

export default CompetitionInfo;
